/**
 * The Todoist mirror.
 *
 * Todoist owns projects, sections and tasks; this pulls them into PocketBase so
 * the rest of the app has rows it can query — cross-project views, timelines,
 * and the reports that join time against tasks. None of that is expressible as
 * a Todoist API call, which is the whole reason a mirror exists.
 *
 * WHY THIS RUNS IN THE BROWSER, NOT A WORKER CRON
 * A full sync writes one PocketBase request per changed record. Cloudflare caps
 * a single Worker invocation at 50 subrequests on the free plan (1000 on paid),
 * so a few hundred tasks would blow the limit mid-sync and leave the mirror
 * half-written. The browser has no such cap, already holds a signed-in
 * PocketBase session, and needs no stored server-side credentials. The cost is
 * that syncing only happens while the app is open — fine for one user, and a
 * cron can be added later without changing the mapping below.
 *
 * SAFETY PROPERTY
 * Only records carrying a `todoistId` are ever touched. Anything you create by
 * hand in Project Hub has no `todoistId` and is invisible to this code — it is
 * never updated and never deleted by a sync.
 */

import { pb } from "./pocketbase";
import { nestingFixes } from "./projectNesting";
import { applyInheritedClients } from "./inheritClients";
import { missingProjects, mirroredStatus } from "./projectStatusSync";
import { todoistGet } from "./todoistClient";
import type { Item, ItemStatus, ItemType, Phase, Priority, Project } from "./types";

/* -- Todoist wire shapes (only the fields we map) --------------------------- */

interface WireProject {
  id: string;
  name: string;
  color: string;
  parent_id: string | null;
  is_archived: boolean;
  child_order?: number;
  /** Todoist's own Inbox. The field has been spelled both ways across API
   *  versions, so both are read rather than betting on one. */
  is_inbox_project?: boolean;
  inbox_project?: boolean;
}
interface WireSection {
  id: string;
  project_id: string;
  name: string;
  section_order: number;
}
interface WireTask {
  id: string;
  project_id: string;
  section_id: string | null;
  parent_id: string | null;
  content: string;
  description: string;
  priority: number;
  labels: string[];
  child_order: number;
  checked: boolean;
  completed_at: string | null;
  due: { date: string; is_recurring: boolean } | null;
  deadline: { date: string } | null;
}

/* -- Vocabulary mapping ------------------------------------------------------ */

/** Todoist's palette, so a mirrored project keeps the colour you gave it there. */
const TODOIST_COLORS: Record<string, string> = {
  berry_red: "#B8256F", red: "#DB4035", orange: "#FF9933", yellow: "#FAD000",
  olive_green: "#AFB83B", lime_green: "#7ECC49", green: "#299438", mint_green: "#6ACCBC",
  teal: "#158FAD", sky_blue: "#14AAF5", light_blue: "#96C3EB", blue: "#4073FF",
  grape: "#884DFF", violet: "#AF38EB", lavender: "#EB96EB", magenta: "#E05194",
  salmon: "#FF8D85", charcoal: "#808080", grey: "#B8B8B8", taupe: "#CCAC93",
};

/** Todoist priority is inverted from how its UI reads: 4 is urgent, 1 is normal. */
const PRIORITY: Record<number, Priority> = { 4: "urgent", 3: "high", 2: "medium", 1: "low" };

/**
 * Todoist has no notion of item type or workflow status, so labels carry them.
 * Matching is case-insensitive and the label is kept as well, so nothing is
 * lost if you later want to filter on it directly.
 */
const TYPE_LABELS: Record<string, ItemType> = { deliverable: "deliverable", milestone: "milestone" };
const STATUS_LABELS: Record<string, ItemStatus> = {
  doing: "doing", "in-progress": "doing", inprogress: "doing",
  waiting: "waiting", blocked: "blocked",
};
/** A task labelled this way shows on the client report. */
const CLIENT_LABEL = "client";

/** The home for tasks that sit in a project without a section. */
const NO_SECTION = "No section";

function typeFor(labels: string[]): ItemType {
  for (const l of labels ?? []) {
    const hit = TYPE_LABELS[l.toLowerCase()];
    if (hit) return hit;
  }
  return "task";
}

function statusFor(task: WireTask): ItemStatus {
  // Two signals, because they don't always agree. The active-tasks endpoint
  // marks completion with `checked`; the completed-tasks endpoint is built
  // around `completed_at` and does not reliably set `checked`. Trusting only
  // `checked` would file every historical completion back as "not started".
  if (task.checked || task.completed_at) return "done";
  for (const l of task.labels ?? []) {
    const hit = STATUS_LABELS[l.toLowerCase()];
    if (hit) return hit;
  }
  return "todo";
}

/** Todoist dates can be "2026-09-04" or a full timestamp; PocketBase wants a day. */
/**
 * A date for PocketBase: the day, or null.
 *
 * NULL, not "". An empty string is at best ambiguous on a `date` column, and a
 * patch that cannot clear a field is a patch that cannot fix bad data — an
 * item whose `due` was written by an older, wrong mapping would keep that value
 * through every future sync, because every future sync only ever asked to set
 * it to "". `null` clears it unambiguously.
 */
const day = (d: string | null | undefined): string | null => (d ? d.slice(0, 10) : null);

/* -- Result ------------------------------------------------------------------ */

export interface SyncCounts {
  created: number;
  updated: number;
  deleted: number;
}
export interface SyncResult {
  projects: SyncCounts;
  phases: SyncCounts;
  items: SyncCounts;
  /** Time entries whose task or project vanished from Todoist this run. */
  orphanedEntries: number;
  finishedAt: string;
  errors: string[];
}

const zero = (): SyncCounts => ({ created: 0, updated: 0, deleted: 0 });

/**
 * Run tasks with bounded concurrency. Sequential is too slow for a few hundred
 * records; unbounded opens hundreds of sockets at once and PocketBase starts
 * refusing them.
 */
async function mapLimit<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      await fn(items[index]);
    }
  });
  await Promise.all(workers);
}

/* -- The sync ---------------------------------------------------------------- */

export async function runTodoistSync(userId: string): Promise<SyncResult> {
  const errors: string[] = [];
  const result: SyncResult = {
    projects: zero(), phases: zero(), items: zero(),
    orphanedEntries: 0, finishedAt: "", errors,
  };

  const [wireProjects, wireArchived, wireSections, wireTasks, wireCompleted] = await Promise.all([
    todoistGet<WireProject[]>("projects"),
    // Null on failure, which stops this run deleting any project: without the
    // archived list, an archived project looks deleted (lib/projectStatusSync).
    todoistGet<WireProject[]>("archived-projects").catch((e: unknown) => {
      errors.push(`Archived projects: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }),
    todoistGet<WireSection[]>("sections"),
    todoistGet<WireTask[]>("tasks"),
    // Completions are a separate endpoint and only reach ~3 months back, which
    // is exactly why they are mirrored rather than queried at report time. A
    // failure here must not abort the rest of the sync.
    todoistGet<WireTask[]>("completed").catch((e: unknown) => {
      errors.push(`Completed tasks: ${e instanceof Error ? e.message : String(e)}`);
      return [] as WireTask[];
    }),
  ]);

  // A task can appear in both lists after being completed mid-sync; last wins.
  const tasksById = new Map<string, WireTask>();
  for (const t of [...wireTasks, ...wireCompleted]) tasksById.set(t.id, t);
  const tasks = [...tasksById.values()];

  /* -- Projects ------------------------------------------------------------- */

  const existingProjects = await pb.collection("projects").getFullList<Project>({
    filter: 'todoistId != ""',
  });
  const projectByTodoistId = new Map(existingProjects.map((p) => [p.todoistId!, p]));

  const archivedTodoistIds = wireArchived ? new Set(wireArchived.map((p) => p.id)) : null;
  for (const wp of [...wireProjects, ...(wireArchived ?? [])]) {
    const archived = archivedTodoistIds?.has(wp.id) ?? false;
    const found = projectByTodoistId.get(wp.id);
    // An archived project is only followed once it is mirrored; one archived
    // before it was ever synced stays out.
    if (archived && !found) continue;
    const status = mirroredStatus(found?.status, archived);
    // Todoist's Inbox mirrors as an ordinary project. It is NOT tagged
    // `kind: "inbox"` — planner hides that kind from the projects list, and
    // with the dedicated Inbox view gone there would be nowhere left to see
    // what is in it.
    const patch = {
      name: wp.name,
      color: TODOIST_COLORS[wp.color] ?? "#4A4E69",
      todoistId: wp.id,
      todoistParentId: wp.parent_id ?? "",
      ...(status ? { status } : null),
    };
    try {
      if (found) {
        const saved = await pb.collection("projects").update<Project>(found.id, patch);
        projectByTodoistId.set(wp.id, saved);
        result.projects.updated++;
      } else {
        const saved = await pb.collection("projects").create<Project>({
          ...patch,
          user: userId,
          // Defaults for the enrichment half. Work, because this account's
          // Todoist is work-first; change it per project in the app.
          category: "work",
          currency: "USD",
          billableDefault: true,
          description: "",
          scope: "",
        } as unknown as Partial<Project>);
        projectByTodoistId.set(wp.id, saved);
        result.projects.created++;
      }
    } catch (e) {
      errors.push(`Project "${wp.name}": ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // Nesting resolves in a second pass: a child can be created before its
  // parent, so the PocketBase id it needs may not have existed on the first.
  // nestingFixes() checks every mirrored project in both directions — into a
  // parent, and back out to the top level — see lib/projectNesting.
  await mapLimit(nestingFixes([...projectByTodoistId.values()]), 6, async (fix) => {
    try {
      await pb.collection("projects").update(fix.id, {
        parent: fix.parent,
        ...(fix.clearFolder ? { folderId: null } : null),
      });
    } catch (e) {
      errors.push(`Nesting "${fix.name}": ${e instanceof Error ? e.message : String(e)}`);
    }
  });

  // After nesting, so a project just moved under a parent takes that parent's
  // client. Only fills a project with none; never overwrites.
  await applyInheritedClients(errors).catch((e: unknown) => {
    errors.push(`Sub-project clients: ${e instanceof Error ? e.message : String(e)}`);
  });

  /* -- Phases (Todoist sections) -------------------------------------------- */

  const existingPhases = await pb.collection("phases").getFullList<Phase>({
    filter: 'todoistId != ""',
  });
  const phaseByTodoistId = new Map(existingPhases.map((p) => [p.todoistId!, p]));

  for (const ws of wireSections) {
    const project = projectByTodoistId.get(ws.project_id);
    if (!project) continue; // its project failed above; the error is already recorded
    const patch = {
      project: project.id,
      name: ws.name,
      position: ws.section_order ?? 0,
      todoistId: ws.id,
    };
    try {
      const found = phaseByTodoistId.get(ws.id);
      const saved = found
        ? await pb.collection("phases").update<Phase>(found.id, patch)
        : await pb.collection("phases").create<Phase>({ ...patch, color: project.color } as unknown as Partial<Phase>);
      phaseByTodoistId.set(ws.id, saved);
      found ? result.phases.updated++ : result.phases.created++;
    } catch (e) {
      errors.push(`Section "${ws.name}": ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // `items.phase` is a required relation, but a Todoist task can sit in a
  // project with no section at all. Those projects get one implicit phase so
  // the task still has somewhere to live.
  const needsCatchAll = new Set(
    tasks.filter((t) => !t.section_id).map((t) => t.project_id),
  );
  const catchAllByProject = new Map<string, Phase>();
  for (const todoistProjectId of needsCatchAll) {
    const project = projectByTodoistId.get(todoistProjectId);
    if (!project) continue;
    const key = `nosection:${todoistProjectId}`;
    let phase = phaseByTodoistId.get(key);
    if (!phase) {
      try {
        phase = await pb.collection("phases").create<Phase>({
          project: project.id, name: NO_SECTION, color: project.color,
          position: -1, todoistId: key,
        } as unknown as Partial<Phase>);
        phaseByTodoistId.set(key, phase);
        result.phases.created++;
      } catch (e) {
        errors.push(`"${NO_SECTION}" for ${project.name}: ${e instanceof Error ? e.message : String(e)}`);
        continue;
      }
    }
    catchAllByProject.set(todoistProjectId, phase);
  }

  /* -- Items (Todoist tasks) ------------------------------------------------ */

  const existingItems = await pb.collection("items").getFullList<Item>({
    filter: 'todoistId != ""',
  });
  const itemByTodoistId = new Map(existingItems.map((i) => [i.todoistId!, i]));

  await mapLimit(tasks, 6, async (wt) => {
    const phase = wt.section_id
      ? phaseByTodoistId.get(wt.section_id)
      : catchAllByProject.get(wt.project_id);
    if (!phase) return; // project or section failed above

    const labels = wt.labels ?? [];
    const patch = {
      phase: phase.id,
      title: wt.content,
      notes: wt.description ?? "",
      type: typeFor(labels),
      status: statusFor(wt),
      priority: PRIORITY[wt.priority] ?? "medium",
      // The mapping the whole timeline rests on: Todoist's date is when you
      // plan to work on it (start), its deadline is the hard finish (due).
      start: day(wt.due?.date),
      due: day(wt.deadline?.date),
      isRecurring: wt.due?.is_recurring ?? false,
      completedAt: wt.completed_at ?? null,
      labels,
      clientVisible: labels.some((l) => l.toLowerCase() === CLIENT_LABEL),
      position: wt.child_order ?? 0,
      todoistId: wt.id,
      todoistParentId: wt.parent_id ?? "",
    };

    try {
      const found = itemByTodoistId.get(wt.id);
      if (found) {
        const saved = await pb.collection("items").update<Item>(found.id, patch);
        itemByTodoistId.set(wt.id, saved);
        result.items.updated++;
      } else {
        const saved = await pb.collection("items").create<Item>(patch as unknown as Partial<Item>);
        itemByTodoistId.set(wt.id, saved);
        result.items.created++;
      }
    } catch (e) {
      errors.push(`Task "${wt.content}": ${e instanceof Error ? e.message : String(e)}`);
    }
  });

  /* -- Deletions ------------------------------------------------------------ */

  const liveTaskIds = new Set(tasks.map((t) => t.id));
  const liveSectionIds = new Set(wireSections.map((s) => s.id));
  const liveProjectIds = new Set(wireProjects.map((p) => p.id));

  // An archived project's sections and tasks are missing from Todoist's active
  // lists too; they are kept with it, not deleted as gone.
  const { deleted: goneProjects, kept: keptProjectIds } = missingProjects(
    existingProjects, liveProjectIds, archivedTodoistIds,
  );
  const projectOfPhase = new Map([...phaseByTodoistId.values(), ...existingPhases].map((p) => [p.id, p.project]));

  /**
   * Absent from Todoist AND still open.
   *
   * `liveTaskIds` is active tasks plus completions from the last ~30 days, so a
   * task finished 31 days ago appears in neither list — and treating that as
   * "deleted" was destroying completion history on every single sync. That is
   * the exact history the mirror exists to hold: Todoist's own API only serves
   * ~3 months of completions, so once it ages out of both the window and the
   * mirror, it is gone for good.
   *
   * The same rule covers a worse case. The completed fetch is deliberately
   * non-fatal and returns [] on error, which made EVERY completed task look
   * deleted — one transient failure on that endpoint would wipe the lot.
   *
   * A completion is history, not live state. Its absence from a 30-day window
   * says nothing about whether it still exists, so it is never deleted here.
   * A completed task genuinely removed in Todoist therefore lingers; that is
   * what the purge action in Settings is for.
   */
  const goneItems = existingItems.filter((i) => {
    if (liveTaskIds.has(i.todoistId!)) return false;
    if (keptProjectIds.has(projectOfPhase.get(i.phase) ?? "")) return false;
    return !(i.completedAt || i.status === "done");
  });
  const gonePhases = existingPhases.filter(
    (p) => !liveSectionIds.has(p.todoistId!) && !p.todoistId!.startsWith("nosection:") && !keptProjectIds.has(p.project),
  );

  // Flag before deleting: once the item row is gone there is no way to find the
  // entries that pointed at it. The hours themselves are never deleted — the
  // relations are cascadeDelete:false precisely so this stays possible.
  result.orphanedEntries = await orphanTimeEntries(goneItems, goneProjects, errors);

  await mapLimit(goneItems, 6, async (i) => {
    try { await pb.collection("items").delete(i.id); result.items.deleted++; }
    catch (e) { errors.push(`Removing "${i.title}": ${e instanceof Error ? e.message : String(e)}`); }
  });
  await mapLimit(gonePhases, 6, async (p) => {
    try { await pb.collection("phases").delete(p.id); result.phases.deleted++; }
    catch (e) { errors.push(`Removing phase "${p.name}": ${e instanceof Error ? e.message : String(e)}`); }
  });
  await mapLimit(goneProjects, 6, async (p) => {
    try { await pb.collection("projects").delete(p.id); result.projects.deleted++; }
    catch (e) { errors.push(`Removing project "${p.name}": ${e instanceof Error ? e.message : String(e)}`); }
  });

  /* -- Sync state ----------------------------------------------------------- */

  result.finishedAt = new Date().toISOString();
  await recordSyncState(userId, result, errors);
  return result;
}

/**
 * Mark time logged against work that no longer exists in Todoist, keeping the
 * hours and a snapshot of what they were for. The brief is explicit that
 * deleting a task must never delete its time.
 */
async function orphanTimeEntries(
  goneItems: Item[],
  goneProjects: Project[],
  errors: string[],
): Promise<number> {
  if (!goneItems.length && !goneProjects.length) return 0;

  const titleById = new Map(goneItems.map((i) => [i.id, i.title]));
  const clauses = [
    ...goneItems.map((i) => `item = "${i.id}"`),
    ...goneProjects.map((p) => `project = "${p.id}"`),
  ];

  try {
    const affected = await pb.collection("time_entries").getFullList<{
      id: string; item?: string; titleSnapshot?: string;
    }>({ filter: clauses.join(" || ") });

    await mapLimit(affected, 6, async (entry) => {
      await pb.collection("time_entries").update(entry.id, {
        orphaned: true,
        // Only fill the snapshot if it is empty, so a re-run never overwrites
        // a title captured when the task still existed.
        titleSnapshot: entry.titleSnapshot || titleById.get(entry.item ?? "") || "",
      });
    });
    return affected.length;
  } catch (e) {
    errors.push(`Flagging orphaned time: ${e instanceof Error ? e.message : String(e)}`);
    return 0;
  }
}

async function recordSyncState(userId: string, result: SyncResult, errors: string[]): Promise<void> {
  const patch = {
    user: userId,
    lastSyncedAt: result.finishedAt,
    lastError: errors.slice(0, 3).join(" | "),
  };
  try {
    const existing = await pb.collection("sync_state").getFullList<{ id: string }>({ perPage: 1 });
    if (existing.length) await pb.collection("sync_state").update(existing[0].id, patch);
    else await pb.collection("sync_state").create(patch);
  } catch {
    // Losing the bookkeeping row must not fail a sync that otherwise worked.
  }
}


/* -- Deliberate cleanup ------------------------------------------------------ */

export interface StaleReport {
  /** Completed items Todoist no longer has. Kept by the sync on purpose. */
  staleItems: { id: string; title: string }[];
  /** Time entries whose task was deleted. Their hours still roll up to the project. */
  orphanedEntries: number;
}

/**
 * What a purge would remove.
 *
 * The sync never deletes a completed task, because a completion that has aged
 * out of Todoist's ~3-month window is indistinguishable from one that was
 * deleted — and guessing wrong loses history permanently. That is the right
 * default, but it means a project you deleted and re-imported leaves its
 * finished work behind. This is the deliberate way to clear it: counted and
 * confirmed, never automatic.
 */
export async function findStale(): Promise<StaleReport> {
  const [wireTasks, wireCompleted] = await Promise.all([
    todoistGet<{ id: string }[]>("tasks"),
    todoistGet<{ id: string }[]>("completed"),
  ]);
  const live = new Set([...wireTasks, ...wireCompleted].map((t) => t.id));

  const existing = await pb.collection("items").getFullList<Item>({ filter: 'todoistId != ""' });
  const staleItems = existing
    .filter((i) => !live.has(i.todoistId!))
    .map((i) => ({ id: i.id, title: i.title }));

  const orphaned = await pb.collection("time_entries").getFullList<{ id: string }>({
    filter: "orphaned = true",
  });

  return { staleItems, orphanedEntries: orphaned.length };
}

/**
 * Remove what findStale() reported.
 *
 * Time entries are deleted only when explicitly asked for: their hours are
 * already attributed to the project independently of the task, so keeping them
 * costs nothing and deleting them changes totals you may have invoiced.
 */
export async function purgeStale(opts: { items: boolean; entries: boolean }): Promise<{ items: number; entries: number }> {
  const report = await findStale();
  let items = 0;
  let entries = 0;

  if (opts.items) {
    await mapLimit(report.staleItems, 6, async (i) => {
      try { await pb.collection("items").delete(i.id); items++; } catch { /* already gone */ }
    });
  }

  if (opts.entries) {
    const rows = await pb.collection("time_entries").getFullList<{ id: string }>({ filter: "orphaned = true" });
    await mapLimit(rows, 6, async (r) => {
      try { await pb.collection("time_entries").delete(r.id); entries++; } catch { /* already gone */ }
    });
  }

  return { items, entries };
}
